import { useMemo, useState } from 'react';
import { css, type Theme } from '@emotion/react';
import { ArrowClockwise, CaretDown, CaretUp, DotsThree, MagnifyingGlass } from '@phosphor-icons/react';
import { AccessRights } from 'common/account/AccessRights';
import { useUser } from 'client/lib/User';
import type { OnLog } from 'client/state/LogState';
import { Button } from 'client/components/Button';
import { DropdownMenu, type DropdownMenuItemProps } from 'client/components/DropdownMenu';
import { statusItemStateToTagProps } from 'client/components/Status/statusUtils';
import { StatusTreeMessage } from 'client/components/StatusTree/components/StatusTreeMessage';
import { StatusTreeTagIcon } from 'client/components/StatusTree/components/StatusTreeTagIcon';
import { Tag } from 'client/components/Tag';
import { Time } from 'client/components/Time';
import { Tooltip } from 'client/components/Tooltip';
import { Typography } from 'client/components/Typography';
import type { ComponentStatusTree } from './componentsStatusUtils';
import { ComponentSubContainers } from './ComponentSubContainers';
import { useContainerActions } from './useContainerActions';

interface ComponentContainerStatusProps {
  deploymentID: number;
  deploymentUrl: string;
  componentIdentifier: string;
  item: ComponentStatusTree['containers'][number];
  onLog?: OnLog;
}

export const ComponentContainerStatus = ({
  deploymentID,
  deploymentUrl,
  componentIdentifier,
  item,
  onLog,
}: ComponentContainerStatusProps) => {
  const { name, itemStateID, isReady, restartCount, timeLastReadyChange, readyMessage, timeStart } = item;
  const [expand, setExpand] = useState(false);
  const { hasAccessRight } = useUser();

  const isAdmin = hasAccessRight(AccessRights.ADMIN_PAGES);

  const { handleRestart, handleDescribe } = useContainerActions({
    deploymentID,
    deploymentUrl,
    namespace: item.namespace,
    pod: item.name,
    onLog,
  });

  const menuItems = useMemo<DropdownMenuItemProps[]>(
    () => [
      {
        id: 'restart',
        title: 'Restart',
        subtitle: 'Restart this specific pod',
        icon: <ArrowClockwise />,
        onClick: handleRestart,
      },
      ...(isAdmin
        ? [
            {
              id: 'describe',
              title: 'Describe',
              subtitle: 'List information about this pod',
              icon: <MagnifyingGlass />,
              onClick: handleDescribe,
            },
          ]
        : []),
    ],
    [isAdmin, handleRestart, handleDescribe]
  );

  const tagProps = useMemo(
    () => ({
      ...statusItemStateToTagProps[itemStateID],
      children: (
        <>
          <StatusTreeTagIcon itemStateID={itemStateID} isReady={isReady} />
          {name}
          {!expand && <CaretDown weight="bold" />}
          {expand && <CaretUp weight="bold" />}
        </>
      ),
    }),
    [itemStateID, name, expand]
  );

  return (
    <li css={containersStyle}>
      <div>
        <Tag noWrap {...tagProps} role="button" onClick={() => setExpand((state) => !state)} css={statusTagStyle} />
        <div>
          <Typography variant="body2">
            <span />
            <span css={infoStyle}>
              <Time prefix="Started " dateTime={timeLastReadyChange || timeStart} />
              {restartCount > 0 && (
                <Tooltip title="Restart count" side="right">
                  <Tag type="grey" css={restartTagStyle}>
                    <ArrowClockwise weight="bold" />
                    {restartCount}
                  </Tag>
                </Tooltip>
              )}
            </span>
          </Typography>
        </div>
        <DropdownMenu items={menuItems}>
          <Button tiny icon={<DotsThree weight="bold" size={16} />} css={menuButtonStyle} />
        </DropdownMenu>
      </div>
      {expand && (
        <ComponentSubContainers
          deploymentID={deploymentID}
          deploymentUrl={deploymentUrl}
          namespace={componentIdentifier}
          pod={item.name}
          containers={item.containers || []}
          initContainers={item.initContainers || []}
          onLog={onLog}
        />
      )}
      <StatusTreeMessage message={readyMessage} />
    </li>
  );
};

const containersStyle = (theme: Theme) => css`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 24px;

  &::before {
    content: '';
    position: absolute;
    display: block;
    width: 4px;
    height: 100%;
    background-color: ${theme.color.green[100]};
    border-radius: 16px;
    left: -30px;
  }

  &:not(:first-of-type) {
    margin-top: 14px;
  }

  > div:first-of-type {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 24px;

    label {
      flex-shrink: 0;

      > svg {
        margin-right: 4px;
      }
    }

    > div {
      display: flex;
      flex-direction: column;
      flex: 1;

      > p {
        display: flex;
        justify-content: space-between;
      }
    }
  }
`;

const infoStyle = css`
  display: flex;
  gap: 6px;

  time {
    opacity: 0.6;
  }
`;

const statusTagStyle = css`
  padding-right: 0;
  max-width: 55%;
  user-select: none;
  cursor: pointer;

  > svg:first-of-type {
    margin-left: -1px;
  }

  > svg:last-of-type {
    margin-left: 2px;
  }
`;

const restartTagStyle = css`
  margin-right: 0 !important;

  > svg {
    margin-left: -1px;
  }
`;

const menuButtonStyle = css`
  aspect-ratio: 1;

  > .icon {
    margin-top: -2px !important;
  }
`;
